'use client';

import { GlobalAccountContext, useGlobalAccountContext } from '@/components/AccountPicker';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import authClient, { type Session } from '@/api/auth/client';
import { useAccount } from '@/hooks/accountHooks';
import { useRouter } from 'next/navigation';
import { ToastContext } from '@/components/Toasts';
import MiniSpinner from '@/components/MiniSpinner';
import * as R from 'remeda';
import { AccessLevel, AccountDTO, doesUserHaveAccess } from '@repo/common/schemas/models';
import { ResponseError } from '@/api/trpc';
import { WithRequired } from '@repo/common/interfaces';
import { exhaustiveSwitchCheck } from '@repo/common/utils';
import { Account } from '@/data/interfaces';
import { Feature, useFeatureGuard } from '@/hooks/useFeatureAvailability';
import { AUTH_ERRORS, type AuthError } from '@repo/api/auth/constants';
import { redirectToLoginPage } from '@/api/auth/authUtils';

// ------------------------------------------------------------------------------------------------------------------ //
//region Types

type AuthHookOptions = Readonly<{
  goToLoginIfUnauthenticated?: boolean;
  goBackIfUnauthorized?: boolean;
}>;
type SessionResultErrorDetail = {
  error: Error | ResponseError;
  errorType: AuthError;
  errorHandled?: boolean;
};
interface _BaseSessionResult {
  isLoading: boolean;
  session?: Session | null;
  user?: Session['user'] | null;
  errorDetail?: SessionResultErrorDetail;
  errorHandled?: boolean;
}
type LoadingSessionResult = _BaseSessionResult & { isLoading: true };
type InvalidSessionResult = WithRequired<_BaseSessionResult, 'errorDetail'>;
interface ValidSessionResult extends _BaseSessionResult {
  isLoading: false;
  session: Session;
  user: Session['user'];
  errorDetail?: undefined;
}
type SessionResult = LoadingSessionResult | InvalidSessionResult | ValidSessionResult;
type ClientAuthResult = SessionResult &
  ((ValidSessionResult & { account: AccountDTO }) | { account?: null });

interface AuthRequirements {
  requiredRole?: AccessLevel;
  requiredFeature?: Feature;
}

interface AuthLayoutProps extends AuthRequirements {
  children: ReactNode;
}

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region React Components

// Wrapper for pages hidden behind admin auth
export function AdminAuthLayout({ children }: { children: ReactNode }) {
  const result = useHandledAuthResult(useAdminAuth(), { goBackIfUnauthorized: false });
  if (result.isLoading) {
    return <MiniSpinner message={'Loading...'} />;
  }
  if (result.errorDetail) {
    return <div>Error: {result.errorDetail?.error?.message || 'User not found'}</div>;
  }
  return <>{children}</>;
}

// Layout for any account-specific pages
// Only load the child pages if the account has loaded successfully OR if an internal Toro user is logged in
export function ClientAuthLayout(props: AuthLayoutProps) {
  const { children, requiredFeature, requiredRole } = props;
  const { account, isLoading, errorDetail, user } = useHandledAuthResult(
    useClientAuth({ requiredRole, requiredFeature })
  );

  if (isLoading) {
    return <MiniSpinner message={'Loading account...'} />;
  }
  // if (isInternalUser && !error) {
  //     return <div>An account must be selected to view this page</div>;
  // }
  if (errorDetail || (!account && !user?.isInternal)) return <div>Error loading account</div>;

  return (
    <>
      <GlobalAccountProvider globalAccount={account} locked={!user?.isInternal}>
        {children}
      </GlobalAccountProvider>
    </>
  );
}

// Layout for pages restricted to clients with the "manager" role or higher
export function ManagerAuthLayout(props: Omit<AuthLayoutProps, 'requiredRole'>) {
  const { children, requiredFeature } = props;
  return (
    <ClientAuthLayout requiredFeature={requiredFeature} requiredRole={'manager'}>
      {children}
    </ClientAuthLayout>
  );
}

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Global Account Provider

export function GlobalAccountProvider({
  children,
  globalAccount = null,
  locked = false,
}: {
  children: ReactNode;
  globalAccount?: Account | null;
  locked?: boolean;
}) {
  const { account: userAccount, user, session } = useClientAuth();
  const parentContextValue = useGlobalAccountContext();
  const isActualInternalUser = Boolean(user?.isInternal);
  const isImpersonation = Boolean(session?.session.impersonatedBy);
  const [nonInternalMode, setSimulateNonInternalUser] = useState(false);
  const simulateNonInternalUser =
    nonInternalMode || parentContextValue?.simulateNonInternalUser || false;
  const [pickedAccount, setSelectedAccount] = useState<Account | null>(null);

  const isLocked = parentContextValue?.isLocked || locked || !isActualInternalUser;
  const parentAccount = parentContextValue?.isClient ? parentContextValue?.selectedAccount : null;
  const isInternalUser = isActualInternalUser && !simulateNonInternalUser && !isLocked;

  const selectedAccount = useMemo(() => {
    if (parentAccount) {
      return parentAccount;
    } else if (isLocked) {
      return globalAccount || userAccount || null;
    } else if (isInternalUser) {
      return null;
    } else {
      return pickedAccount;
    }
  }, [parentAccount, isLocked, isInternalUser, globalAccount, userAccount, pickedAccount]);

  const contextValue: GlobalAccountContext = useMemo(() => {
    return {
      isActualInternalUser,
      simulateNonInternalUser,
      isInternalUser,
      isImpersonation,
      isAdmin: isInternalUser,
      isClient: !isInternalUser,
      isLocked,
      setSimulateNonInternalUser,
      selectedAccount,
      setSelectedAccount,
    };
  }, [
    isInternalUser,
    isActualInternalUser,
    isImpersonation,
    selectedAccount,
    simulateNonInternalUser,
    isLocked,
  ]);

  return (
    <GlobalAccountContext.Provider value={contextValue}>{children}</GlobalAccountContext.Provider>
  );
}

// GlobalAccountContext provider for embedded sessions
export function EmbeddedAccountProvider(params: {
  account?: AccountDTO | null;
  children: ReactNode;
}) {
  const { account, children } = params;
  const contextValue: GlobalAccountContext = useMemo(
    () => ({
      isActualInternalUser: false,
      simulateNonInternalUser: false,
      isInternalUser: false,
      isImpersonation: false,
      isAdmin: false,
      isClient: true,
      isLocked: true,
      setSimulateNonInternalUser: R.doNothing(),
      selectedAccount: account ?? null,
      setSelectedAccount: R.doNothing(),
    }),
    [account]
  );

  return (
    <GlobalAccountContext.Provider value={contextValue}>{children}</GlobalAccountContext.Provider>
  );
}

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Hooks

// Ensures a Toro internal user is logged in, or redirects them away if not
function useAdminAuth(): SessionResult {
  const result = useAuthenticatedSession();
  return useMemo(() => {
    if (result?.user && !result.user.isInternal) {
      return {
        isLoading: false,
        errorDetail: {
          error: new Error('User does not have permission to view this page'),
          errorType: AUTH_ERRORS.UNAUTHORIZED,
        },
      };
    }
    return result;
  }, [result]);
}

// Ensures a user is logged in, and returns their configured account
function useClientAuth(params: AuthRequirements = {}): ClientAuthResult {
  const { requiredRole, requiredFeature } = params;
  const { selectedAccount: globalAccount } = useContext(GlobalAccountContext);
  const sessionResult = useAuthenticatedSession();
  const { user } = sessionResult;
  const accountId = globalAccount?._id || user?.toroAccount || undefined;

  const {
    account,
    isLoading: accountLoading,
    error: accountError,
  } = useAccount(user ? accountId : null);

  const { isLoading: isFeatureGuardLoading } = useFeatureGuard(requiredFeature, accountId);

  const isLoading = accountLoading || sessionResult.isLoading || isFeatureGuardLoading;

  const result: ClientAuthResult = useMemo(() => {
    if (sessionResult.errorDetail) {
      return sessionResult;
    } else if (isLoading) {
      return { ...sessionResult, account: null, isLoading: true };
    } else if (accountError || !account) {
      return {
        ...sessionResult,
        account: null,
        errorDetail: {
          error: accountError || new Error('Unknown error retrieving account'),
          errorType: AUTH_ERRORS.UNEXPECTED_ERROR,
        },
      };
    }
    if (requiredRole) {
      const userRole = sessionResult.user.role;
      if (!sessionResult.user.isInternal && !doesUserHaveAccess(requiredRole, userRole)) {
        return {
          ...sessionResult,
          account: null,
          errorDetail: {
            error: new Error(
              `${R.toTitleCase(requiredRole)} permission is required to view this page`
            ),
            errorType: AUTH_ERRORS.UNAUTHORIZED,
          },
        };
      }
    }
    return { ...sessionResult, isLoading: false, account };
  }, [account, accountError, isLoading, requiredRole, sessionResult]);

  return result;
}

// Ensures a user is logged in, and returns the session and user
function useAuthenticatedSession(): SessionResult {
  const { data: session, isPending, error } = authClient.useSession();
  const result: SessionResult = useMemo(() => {
    if (isPending) return { isLoading: true };
    if (error) {
      return {
        isLoading: false,
        errorDetail: {
          error,
          errorType: AUTH_ERRORS.UNEXPECTED_ERROR,
        },
      };
    }
    const user = session?.user;
    // TODO: On sign-out this triggers a redundant signOut() call via the UNAUTHENTICATED handler
    if (!session || !user) {
      return {
        isLoading: false,
        errorDetail: {
          error: new Error('Not authenticated'),
          errorType: AUTH_ERRORS.UNAUTHENTICATED,
        },
      };
    }

    return { session, user, isLoading: false };

    // switch (status) {
    //   case 'unauthenticated': {
    //     return {
    //       isLoading: false,
    //       errorDetail: {
    //         error: new Error('User is not authenticated'),
    //         errorType: 'UNAUTHENTICATED',
    //       },
    //     };
    //   }
    //   case 'loading': {
    //     return { isLoading: true };
    //   }
    //   case 'authenticated': {
    //     if (!isValidSession(session)) {
    //       return {
    //         isLoading: false,
    //         errorDetail: {
    //           error: new Error('User expected for authenticated session'),
    //           errorType: 'UNEXPECTED_ERROR',
    //         },
    //       };
    //     }
    //     return { session, user: session.user, isLoading: false };
    //   }
    //   default: {
    //     throw exhaustiveSwitchCheck(status);
    //   }
    // }
  }, [session, isPending, error]);
  return result;
}

// Hook to parse the auth result and trigger any configured actions based on the options passed in
function useHandledAuthResult<T extends SessionResult>(result: T, options?: AuthHookOptions): T {
  // Track the last error type we handled, so we don't trigger the handler repeatedly as loading states switch on and off
  const lastHandledErrorTypeRef = useRef<AuthError | null>(null);
  const errorHandler = useAuthErrorHandler(options);

  useEffect(() => {
    const lastHandledErrorType = lastHandledErrorTypeRef.current;
    if (lastHandledErrorType && !result.errorDetail && !result.isLoading) {
      console.log('Resetting lastHandledErrorType on success');
      lastHandledErrorTypeRef.current = null;
    } else if (result.errorDetail && lastHandledErrorType !== result.errorDetail.errorType) {
      lastHandledErrorTypeRef.current = result.errorDetail.errorType;
      errorHandler(result.errorDetail);
    }
  }, [errorHandler, result.errorDetail, result.isLoading]);

  return result;
}

function useAuthErrorHandler(options?: AuthHookOptions) {
  const router = useRouter();
  const { errorToast } = useContext(ToastContext);
  const { goToLoginIfUnauthenticated = true, goBackIfUnauthorized = false } = options || {};
  return useCallback(
    async (errorDetail?: SessionResultErrorDetail | null) => {
      if (!errorDetail || errorDetail.errorHandled || !errorDetail.error) {
        return;
      }
      switch (errorDetail.errorType) {
        case AUTH_ERRORS.UNAUTHENTICATED: {
          console.log('User is not authenticated', errorDetail);
          if (goToLoginIfUnauthenticated) {
            await redirectToLoginPage(errorDetail.error);
          }
          return;
        }
        case AUTH_ERRORS.UNAUTHORIZED: {
          errorToast(
            new Error('User is not allowed to view this page', { cause: errorDetail.error })
          );
          if (goBackIfUnauthorized) {
            console.log('Navigating back to previous page');
            router.back();
          }
          return;
        }
        case AUTH_ERRORS.UNEXPECTED_ERROR:
        default: {
          errorToast('Unexpected auth error');
          console.error('Unexpected auth error', errorDetail.error, errorDetail);
          return;
        }
      }
    },
    [goToLoginIfUnauthenticated, goBackIfUnauthorized, router, errorToast]
  );
}

//endregion
// ------------------------------------------------------------------------------------------------------------------ //

// // Helper
// function isValidSession(session?: Session | null): session is SessionWithUser {
//   return R.isTruthy(session?.user);
// }
